import { toast } from 'react-toastify';
import { useForm, useWatch } from 'react-hook-form';

import { generalSchema } from '../schemas';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useDeletePracticeLogoMutation,
  useEditPracticeMutation,
  useOrganizationsShortQuery,
  usePartnerShortQuery,
  usePracticeLogoQuery,
  useUploadPracticeLogoMutation,
} from '@common/cache';
import { paths } from '@common/types';
import { useContext, useEffect, useState } from 'react';
import { dataURItoBlob } from '@util/image-utils';
import { omit } from 'lodash';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { fillAddressFormValues } from '@util/google-places';
import { AdminPanelDrawerContext } from '@views/pages/Admin/context';
import { checkFormIsDirty } from '@util/checkFormIsDirty.ts';

interface IGeneralFormShape {
  name: string;
  organization_id: string;
  partner_id: string;
  provider_id?: string;
  external: boolean;
  phone_number?: string | null;
  fax_number?: string | null;
  google_places_id?: string | null;
  street: string;
  suite?: string | null;
  city: string;
  state: string;
  county?: string | null;
  zip_code: string;
}

const initialValues: Partial<IGeneralFormShape> = {
  name: '',
  organization_id: '',
  partner_id: '',
  external: false,
  google_places_id: '',
  street: '',
  suite: '',
  city: '',
  state: '',
  county: '',
  zip_code: '',
  fax_number: '',
  phone_number: '',
};

export const useGeneral = (
  practice: paths[`/practice/{record_id}`]['get']['responses']['200']['content']['application/json'],
) => {
  const {
    triggerClose,
    triggerTabChange,
    setIsFormClean,
    handleCloseConfirmationModal,
  } = useContext(AdminPanelDrawerContext);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const { mutateAsync, isPending } = useEditPracticeMutation(practice.id);
  const { data: organizationOptions = [] } = useOrganizationsShortQuery();

  const formMethods = useForm<IGeneralFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<IGeneralFormShape, any, any>(generalSchema),
    defaultValues: {
      ...initialValues,
      ...practice,
      ...practice.address,
      organization_id: practice.organization.id,
      partner_id: practice.partner.id,
    },
  });

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    formState: { isSubmitting, errors, isValid, dirtyFields },
  } = formMethods;

  const org_id = useWatch({
    control: formMethods.control,
    name: 'organization_id',
  });
  const prov_id = useWatch({
    control: formMethods.control,
    name: 'provider_id',
  });

  const isFormDirty =
    checkFormIsDirty(dirtyFields) || prov_id !== practice.provider_id;

  const { data: partnerOptions = [], refetch } = usePartnerShortQuery(
    org_id as string,
  );

  useEffect(() => {
    if (org_id) {
      refetch();
    }
  }, [org_id]);

  useEffect(() => {
    if (triggerClose && isFormDirty) {
      setIsSaveChangesModalOpen(true);
      return;
    }

    if (triggerTabChange && isFormDirty) {
      setIsSaveChangesModalOpen(true);
      return;
    }

    if (isFormDirty) {
      setIsFormClean(false);
      return;
    }

    if (triggerClose && !isFormDirty) {
      setIsFormClean(true);
    }

    if (triggerTabChange && !isFormDirty) {
      setIsFormClean(true);
    }
  }, [triggerClose, isFormDirty, triggerTabChange, dirtyFields]);

  const fetchedLogo = usePracticeLogoQuery({
    practice_id: practice.id,
  });
  const [logo, setLogo] = useState<string>(
    fetchedLogo.data ? URL.createObjectURL(fetchedLogo?.data) : '',
  );

  useEffect(() => {
    if (fetchedLogo.data) {
      setLogo(URL.createObjectURL(fetchedLogo.data as Blob));
    }
  }, [fetchedLogo.data]);

  const uploadLogoMutation = useUploadPracticeLogoMutation(practice.id);

  const handleUploadLogo = async (newLogo: string) => {
    setLogo(newLogo);

    let blob = dataURItoBlob(newLogo);
    let fd = new FormData();
    fd.append('file', blob);

    try {
      toast.info('Logo uploading...');

      await uploadLogoMutation.mutateAsync({
        id: practice.id,
        file: fd,
      });
      toast.success('Logo updated');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const deleteLogoMutation = useDeletePracticeLogoMutation(practice.id);

  const handleDeleteLogo = () => {
    deleteLogoMutation.mutateAsync({ id: practice.id });
  };

  const handleSelectLocation = (place: Partial<google.maps.places.Place>) => {
    fillAddressFormValues({ setValue, place });
  };

  const submitHandler = async (values: IGeneralFormShape) => {
    try {
      await mutateAsync({
        ...omit(values, ['organization', 'partner']),
        practice_id: practice.id,
        country: 'US',
        phone_number: values.phone_number || '',
      });

      toast.success('Changes saved');
      reset(values);
      setIsFormClean(true);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
    handleCloseConfirmationModal();
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsFormClean(true);
  };

  const handleSaveChanges = async () => {
    await onSubmit();
    reset(getValues());
    setIsFormClean(true);
  };

  const onSubmit = handleSubmit(submitHandler);

  return {
    control,
    onSubmit,
    isSubmitEnabled:
      isFormDirty && Object.keys(errors).length === 0 && !isSubmitting,
    isSubmitting,
    logo,
    handleUploadLogo,
    handleDeleteLogo,
    organizationOptions: organizationOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    partnerOptions: partnerOptions.map(o => ({ value: o.id, label: o.name })),
    formMethods,
    setValue,
    isPartnerSelectDisabled: organizationOptions.length > 0 && !org_id,
    handleSelectLocation,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
  };
};
